import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { suggestNextStep } from "./startHereSuggest";

// What the student already knows → the stop and existing destination to start from.
const ENTRY_POINTS = [
  { key: "pick",   when: "I'm not sure what to study yet",              tool: "Fast Match" },
  { key: "place",  when: "I know where I'd like to live",              tool: "City Matcher" },
  { key: "course", when: "I know the course and want to check entry",  tool: "Quick Check" },
  { key: "prove",  when: "I'm ready to compare how well options fit me", tool: "Advanced Search" },
  { key: "shortlist", when: "I've got a few universities in mind",      tool: "Final list" },
  { key: "apply",  when: "I've got my final list",                     tool: "Start applying" },
];

function EntryRow({ entry, station, suggestion }) {
  const reasonId = `journey-start-here-reason-${entry.key}`;
  return (
    <li className={suggestion ? "my-2 rounded-md border-2 bg-white px-3 shadow-sm" : ""} style={suggestion ? { borderColor: station.colour } : undefined}
      data-suggested={suggestion ? "true" : "false"} data-testid={`journey-start-here-item-${entry.key}`}>
      {suggestion && (
        <span className="mt-2 inline-flex items-center gap-1 rounded-full bg-stone-900 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-white"
          data-testid="journey-start-here-suggested-badge">
          Suggested next step
        </span>
      )}
      <Link
        to={station.primary.to}
        data-testid={`journey-start-here-${entry.key}`}
        aria-describedby={suggestion ? reasonId : undefined}
        className={`group flex min-h-[44px] items-center gap-4 py-3 pr-1 hover:bg-white/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2 rounded-sm transition-colors ${suggestion ? "" : "border-b border-stone-200"}`}
      >
        <span className="h-3 w-3 shrink-0 rounded-full ring-2 ring-white" style={{ backgroundColor: station.colour }} aria-hidden />
        <span className={`min-w-0 flex-1 text-[15px] leading-snug ${suggestion ? "font-semibold text-stone-900" : "text-stone-800"}`}>{entry.when}</span>
        <span className="shrink-0 text-right text-[13px] leading-snug">
          <span className="block font-semibold" style={{ color: station.colour }}>{station.label}</span>
          <span className="block text-stone-500">{entry.tool}</span>
        </span>
        <ArrowRight className="h-4 w-4 shrink-0 text-stone-400 transition-transform group-hover:translate-x-0.5" aria-hidden />
      </Link>
      {suggestion && (
        <p id={reasonId} className="pb-3 text-[13px] leading-relaxed text-stone-600" data-testid="journey-start-here-reason">{suggestion.reason}</p>
      )}
    </li>
  );
}

function AllDoneNote() {
  return (
    <p className="mt-4 rounded-md border border-stone-200 bg-white px-4 py-3 text-[13px] leading-relaxed text-stone-700" data-testid="journey-start-here-all-done">
      All six stops are done, so there's no next step you need to take. Revisit any stop, or{" "}
      <Link to="/shortlist" className="underline underline-offset-2 text-[#4C1D95]" data-testid="journey-start-here-review-shortlist">review your shortlist</Link>{" "}
      or{" "}
      <Link to="/realisations" className="underline underline-offset-2 text-[#4C1D95]" data-testid="journey-start-here-review-realisations">look back at your Realisations</Link>.
    </p>
  );
}

export function StartHereGuide({ stations, pillarsCompleted, loaded }) {
  const byKey = Object.fromEntries(stations.map((s) => [s.key, s]));
  const suggestion = loaded ? suggestNextStep(stations, pillarsCompleted) : null;
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 py-8" data-testid="journey-start-here" aria-labelledby="journey-start-here-title">
      <div className="grid gap-8 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:gap-16">
        <div>
          <div className="label-uppercase text-stone-500">Route Finder</div>
          <h2 id="journey-start-here-title" data-testid="journey-start-here-title" className="mt-2 font-editorial text-2xl sm:text-3xl text-stone-900 leading-tight">
            Start here, depending on what you know
          </h2>
          <p className="mt-3 text-sm sm:text-base text-stone-600 leading-relaxed max-w-md">
            You don't have to go in order. Jump in at the stop that fits where you are now. Every stop stays open, so you can come back to any of them.
          </p>
          <p className="mt-4 text-sm text-stone-600 leading-relaxed max-w-md" data-testid="journey-start-here-realisations">
            <span className="font-semibold text-stone-800">Realisations</span> isn't a stop you have to reach. It's somewhere to reflect at any point on what your choices are telling you, and what you share there feeds into Course and Prove.{" "}
            <Link to="/realisations" data-testid="journey-start-here-realisations-link"
              className="underline decoration-2 underline-offset-2 text-[#4C1D95] hover:text-[#3B0764] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4C1D95] rounded-sm">
              Open Realisations
            </Link>
          </p>
        </div>
        <div>
          <ul className="border-t border-stone-200" aria-label="Where to start">
            {ENTRY_POINTS.filter((e) => byKey[e.key]).map((e) => (
              <EntryRow key={e.key} entry={e} station={byKey[e.key]}
                suggestion={suggestion?.key === e.key ? suggestion : null} />
            ))}
          </ul>
          {suggestion?.allDone && <AllDoneNote />}
        </div>
      </div>
    </section>
  );
}
